import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { Box, Button, Card, Grid, Stack, Typography } from "@mui/material";
import Lottie from "react-lottie";
import QRCode from "react-qr-code";
import { assets } from "../../assets";
import { UI, qrPlaceholderSx } from "./constants";
import { DEVICE_STATUS } from "./helpers/DeviceModesConst";

export const DevicePairingCard = ({
  device,
  deviceTrackerUid,
  loading,
  handleCreate,
  handleDelete,
  handleDownloadQR,
  isQRHovered,
  setIsQRHovered,
  qrCodeRef,
  deleted,
  deviceLoaded,
  showStartPairing,
}) => {
  const isLinked = device?.status === DEVICE_STATUS.LINKED;
  const isPending = device?.status === DEVICE_STATUS.PENDING;
  const isUnlinked = device?.status === DEVICE_STATUS.UNLINKED;
  const canShowPairingCode = (isPending || isUnlinked) && !deleted;

  return (
    <Card
      sx={{
        borderRadius: UI.cardRadius,
        border: "none",
        boxShadow: "none",
        p: { xs: "1.25rem", md: "2rem" },
        height: "100%",
      }}
    >
      <Stack spacing="0.5rem" sx={{ mb: { xs: "1rem", md: "2rem" } }}>
        <Stack direction="row" spacing="0.75rem" alignItems="center">
          <QrCodeScannerIcon sx={{ fontSize: "2.5rem", color: UI.primary }} />
          <Typography
            sx={{
              fontSize: { xs: "1.5rem", md: "1.75rem" },
              fontWeight: 800,
              letterSpacing: "-0.02em",
            }}
          >
            Device Pairing
          </Typography>
        </Stack>
        <Typography sx={{ color: UI.textMuted, fontSize: { xs: "0.875rem", md: "1rem" } }}>
          Connect your mobile device to sync team data instantly.
        </Typography>
      </Stack>

      <Grid container spacing={{ xs: "1rem", md: "2rem" }} alignItems="center">
        {/* QR */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              position: "relative",
              p: "0.5rem",
              borderRadius: "1rem",
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              width: "fit-content",
              mx: { xs: "auto", md: 0 },
            }}
          >
            {[
              { top: "-0.0625rem", left: "-0.0625rem", bt: true, bl: true, br: "0.75rem 0 0 0" },
              { top: "-0.0625rem", right: "-0.0625rem", bt: true, brd: true, br: "0 0.75rem 0 0" },
              { bottom: "-0.0625rem", left: "-0.0625rem", bb: true, bl: true, br: "0 0 0 0.75rem" },
              { bottom: "-0.0625rem", right: "-0.0625rem", bb: true, brd: true, br: "0 0 0.75rem 0" },
            ].map((c, idx) => (
              <Box
                key={idx}
                sx={{
                  position: "absolute",
                  width: "2rem",
                  height: "2rem",
                  ...(c.top ? { top: c.top } : {}),
                  ...(c.bottom ? { bottom: c.bottom } : {}),
                  ...(c.left ? { left: c.left } : {}),
                  ...(c.right ? { right: c.right } : {}),
                  borderStyle: "solid",
                  borderWidth: 0,
                  ...(c.bt ? { borderTopWidth: "0.25rem" } : {}),
                  ...(c.bb ? { borderBottomWidth: "0.25rem" } : {}),
                  ...(c.bl ? { borderLeftWidth: "0.25rem" } : {}),
                  ...(c.brd ? { borderRightWidth: "0.25rem" } : {}),
                  borderRadius: c.br,
                  pointerEvents: "none",
                }}
              />
            ))}

            {canShowPairingCode ? (
              <Box
                sx={{
                  width: "12rem",
                  height: "12rem",
                  ...qrPlaceholderSx,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  overflow: "hidden",
                  cursor: "pointer",
                }}
                onMouseEnter={() => setIsQRHovered(true)}
                onMouseLeave={() => setIsQRHovered(false)}
                onClick={handleDownloadQR}
              >
                <Box ref={qrCodeRef} sx={{ p: "0.5rem", bgcolor: "var(--fx-surface-base, #FFFFFF)", borderRadius: "0.75rem" }}>
                  <QRCode value={device.shortUid} size={160} />
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: isQRHovered ? "rgba(0,0,0,0.30)" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background-color 180ms ease",
                    pointerEvents: "none",
                  }}
                >
                  {isQRHovered && <DownloadIcon sx={{ fontSize: "2.5rem", color: "#fff" }} />}
                </Box>
              </Box>
            ) : (
              <Lottie
                options={{
                  animationData: assets.animation.qrAnimation,
                  loop: true,
                  autoplay: true,
                }}
                style={{ width: "12rem", height: "12rem" }}
              />
            )}
          </Box>

          <Box sx={{ mt: "0.75rem", textAlign: "center" }}>
            {isLinked && !deleted && (
              <Typography sx={{ fontSize: "0.75rem", color: UI.textMuted }}>
                Linked •{" "}
                <Box component="span" sx={{ fontFamily: "monospace" }}>
                  {deviceTrackerUid}
                </Box>
              </Typography>
            )}
            {isPending && !deleted && (
              <Typography sx={{ fontSize: "0.75rem", color: UI.textMuted }}>Scan the QR code to pair your device.</Typography>
            )}

            {isUnlinked && !deleted && (
              <Typography sx={{ fontSize: "0.75rem", color: UI.textMuted }}>This device is unlinked. Reuse the QR code to pair it again.</Typography>
            )}

            {deleted && <Typography sx={{ fontSize: "0.75rem", color: UI.textMuted }}>Device tracker deleted.</Typography>}

            {showStartPairing && !deleted && (
              <Typography sx={{ fontSize: "0.75rem", color: UI.textMuted }}>Start pairing to generate a QR code.</Typography>
            )}
          </Box>
        </Grid>

        {/* Steps + actions */}
        <Grid item xs={12} md={6}>
          <Stack spacing="1rem">
            {!showStartPairing ? (
              <Stack spacing="0.75rem">
                {[
                  "Open the mobile app on your smartphone or tablet",
                  'Select the "Enter as a guest" option',
                  "Scan the QR code displayed on the left or input the pairing code",
                ].map((txt, i) => (
                  <Stack key={i} direction="row" spacing="0.75rem" alignItems="flex-start">
                    <Box
                      sx={{
                        mt: "0.125rem",
                        width: "1.5rem",
                        height: "1.5rem",
                        borderRadius: "999rem",
                        bgcolor: "rgba(236, 127, 19, 0.10)",
                        color: UI.primary,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: "0.75rem", // Reduced
                        flex: "0 0 auto",
                      }}
                    >
                      {i + 1}
                    </Box>
                    <Typography sx={{ fontSize: { xs: "0.875rem", md: "1rem" }, color: UI.textMuted, lineHeight: 1.35 }}>{txt}</Typography>
                  </Stack>
                ))}
              </Stack>
            ) : null}

            {isLinked && !deleted && (
              <Stack spacing="0.75rem">
                <Stack direction="row" spacing="0.5rem" alignItems="center">
                  <CheckCircleIcon sx={{ color: "#16a34a", fontSize: "1.25rem" }} />
                  <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>Device Tracker linked</Typography>
                </Stack>

                <Typography sx={{ fontSize: "0.75rem", color: UI.textMuted, wordBreak: "break-all" }}>{deviceTrackerUid}</Typography>

                <Button
                  onClick={handleDelete}
                  disabled={loading}
                  variant="contained"
                  startIcon={<DeleteIcon />}
                  sx={{
                    bgcolor: "#ef4444",
                    "&:hover": { bgcolor: "#dc2626" },
                    borderRadius: "0.5rem",
                    py: "0.6rem",
                    fontWeight: 800,
                    textTransform: "none",
                    width: { xs: "100%", md: "auto" },
                    fontSize: "0.875rem",
                  }}
                >
                  {loading ? "Deleting..." : "Delete"}
                </Button>
              </Stack>
            )}

            {deleted && (
              <Stack spacing="0.5rem" alignItems={{ xs: "center", md: "flex-start" }}>
                <CheckCircleIcon sx={{ color: "#16a34a", fontSize: "2rem" }} />
                <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>Device Tracker deleted</Typography>
              </Stack>
            )}

            {canShowPairingCode && !deleted && (
              <Stack spacing="0.5rem">
                <Stack direction="row" spacing="0.5rem" alignItems="center">
                  <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>Pairing request code created:</Typography>
                </Stack>

                <Typography sx={{ fontSize: "1.2rem", color: UI.primary, lineHeight: 1.5, fontWeight: 800 }}>{device.shortUid}</Typography>
              </Stack>
            )}

            {isUnlinked && !deleted && (
              <Typography sx={{ fontSize: "0.75rem", color: UI.textMuted }}>This device is unlinked. Reuse the QR code to pair it again.</Typography>
            )}

            {showStartPairing && !deleted && (
              <Button
                onClick={handleCreate}
                disabled={loading}
                variant="contained"
                sx={{
                  bgcolor: UI.primary,
                  "&:hover": { bgcolor: "#d6710f" },
                  borderRadius: "0.75rem",
                  py: "0.75rem",
                  fontWeight: 900,
                  fontSize: "1rem",
                  textTransform: "none",
                }}
              >
                {loading ? "Creating..." : "Start Pairing"}
              </Button>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );
};
